import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Code2, FileSpreadsheet, Plus, Upload } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DataTable, EmptyState, ErrorState, LoadingState } from '@/presentation/components/index.js';
import type { DevelopmentController } from '@/presentation/controllers/development-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useDevelopments } from '@/presentation/hooks/use-developments.js';
import { DevelopmentListFilters } from '@/presentation/pages/developments/components/development-list-filters.js';
import { ImportDevelopmentsDialog } from '@/presentation/pages/developments/components/dialogs/import-developments-dialog.js';
import { buildDevelopmentListColumns } from '@/presentation/pages/developments/utils/build-development-list-columns.js';
import { DEVELOPMENT_LIST_STATUSES } from '@/presentation/pages/developments/utils/development-list-statuses.js';
import { resolveCanCreateDevelopment } from '@/presentation/pages/developments/utils/resolve-can-create-development.js';
import { resolveCanImportDevelopment } from '@/presentation/pages/developments/utils/resolve-can-import-development.js';
import { IMPORT_TEMPLATE_FILENAME, IMPORT_TEMPLATE_URL } from '@/shared/constants/import-template.js';
import { ROUTES, toDevelopmentDetail } from '@/shared/constants/routes.js';

interface Props {
    controller: DevelopmentController;
}

export function DevelopmentListPage({ controller }: Props) {
    const { translate, useAuthStore } = useApp();
    const canCreateDevelopment = useAuthStore((s) => resolveCanCreateDevelopment(s.user));
    const canImportDevelopment = useAuthStore((s) => resolveCanImportDevelopment(s.user));
    const navigate = useNavigate();
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const {
        developments,
        isLoading,
        error,
        search,
        setSearch,
        statusFilter,
        setStatusFilter,
        showCanceled,
        setShowCanceled,
        selectedProject,
        reload,
        total,
        page,
        setPage,
        limit,
        setLimit,
        exportExcel,
        exporting,
        exportError,
        importExcel,
        importing,
        importError,
        importSummary,
        selectedImportFile,
        setSelectedImportFile,
        clearImportError
    } = useDevelopments(controller);

    if (!selectedProject && !isLoading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
                    {translate('nav.developments')}
                </Typography>
                <EmptyState icon={<Code2 size={24} />} title={translate('devList.noProject')} message={translate('devList.noProjectMsg')} />
            </Box>
        );
    }
    if (isLoading && developments.length === 0) {
        return <LoadingState />;
    }
    if (error) {
        return <ErrorState message={error} onRetry={reload} />;
    }

    const columns = buildDevelopmentListColumns(translate);

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h5" fontWeight={700}>
                    {translate('nav.developments')}
                </Typography>
                <Stack direction="row" spacing={1}>
                    {canImportDevelopment && (
                        <Button
                            variant="outlined"
                            startIcon={<Upload size={18} />}
                            onClick={() => {
                                clearImportError();
                                setImportDialogOpen(true);
                            }}
                            disabled={importing || !selectedProject}
                            data-testid="import-excel-button"
                        >
                            {importing ? translate('devList.importing') : translate('devList.importExcel')}
                        </Button>
                    )}
                    <Button variant="outlined" startIcon={<FileSpreadsheet size={18} />} onClick={() => void exportExcel()} disabled={exporting || !selectedProject || total === 0}>
                        {exporting ? translate('devList.exporting') : translate('devList.exportExcel')}
                    </Button>
                    {canCreateDevelopment && (
                        <Button variant="contained" startIcon={<Plus size={18} />} onClick={() => navigate(ROUTES.DEVELOPMENT_NEW)} sx={{ color: 'white' }}>
                            {translate('devList.newDevelopment')}
                        </Button>
                    )}
                </Stack>
            </Stack>
            {exportError && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                    {translate('devList.exportError')}
                </Typography>
            )}
            {importError && (
                <Typography variant="body2" color="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} data-testid="import-page-error">
                    {importError}
                </Typography>
            )}
            {importSummary && (
                <Alert severity="info" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={clearImportError} data-testid="import-summary">
                    {importSummary}
                </Alert>
            )}
            <DevelopmentListFilters
                search={search}
                onSearchChange={setSearch}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                showCanceled={showCanceled}
                onShowCanceledChange={setShowCanceled}
                translate={translate}
                statuses={DEVELOPMENT_LIST_STATUSES}
            />
            {developments.length === 0 ? (
                <EmptyState icon={<Code2 size={24} />} title={translate('devList.noDevsInProject')} message={translate('devList.noDevsInProjectMsg')} />
            ) : (
                <DataTable
                    columns={columns}
                    rows={developments}
                    getRowKey={(d) => d.id}
                    onRowClick={(d) => navigate(toDevelopmentDetail(d.id))}
                    page={page - 1}
                    rowsPerPage={limit}
                    total={total}
                    onPageChange={(nextPage) => setPage(nextPage + 1)}
                    onRowsPerPageChange={setLimit}
                    rowsPerPageOptions={[20, 50, 100]}
                    loading={isLoading}
                />
            )}
            <ImportDevelopmentsDialog
                open={importDialogOpen}
                submitting={importing}
                error={importError}
                selectedFile={selectedImportFile}
                onClose={() => {
                    setImportDialogOpen(false);
                    setSelectedImportFile(null);
                    clearImportError();
                }}
                onFileSelected={setSelectedImportFile}
                onClearFile={() => {
                    setSelectedImportFile(null);
                    clearImportError();
                }}
                templateHref={IMPORT_TEMPLATE_URL}
                templateFilename={IMPORT_TEMPLATE_FILENAME}
                onImport={() => {
                    if (!selectedImportFile) {
                        return;
                    }
                    void importExcel(selectedImportFile)
                        .then((result) => {
                            if (result) {
                                setImportDialogOpen(false);
                            }
                        })
                        .catch(() => {
                            // Erro já tratado internamente pelo hook; garante que não haja rejeição não capturada
                        });
                }}
                labels={{
                    importExcelTitle: translate('devList.importExcelTitle'),
                    downloadTemplate: translate('devList.downloadTemplate'),
                    dragOrClickUpload: translate('devList.dragOrClickUpload'),
                    acceptedImportFormats: translate('devList.acceptedImportFormats'),
                    invalidImportFormat: translate('devList.invalidImportFormat'),
                    importFileTooLarge: translate('devList.importFileTooLarge'),
                    selectedFile: translate('devList.selectedFile'),
                    cancel: translate('common.cancel'),
                    importExcel: translate('devList.importExcel'),
                    importing: translate('devList.importing'),
                    removeFile: translate('common.remove')
                }}
            />
        </Box>
    );
}
